/**
 * RuleLibraryGallery — Day 7-8 deliverable.
 *
 * A richer replacement for the bare-list library tab: card gallery with
 * full-text search, tag / rule-type filters, and a reuse dialog that spawns
 * a draft rule in the current project. No "Retire" action here — retiring a
 * shared KB entry is a higher-blast-radius, catalog-curation action than a
 * per-project button should trigger; the Findings tab's per-finding "Ignore"
 * is the right model for "stop showing me this" if that's ever needed here.
 *
 * Data shape matches `rulesApi.library.list` — see `frontend/src/api/rules.ts`.
 */

import { useMemo, useState } from "react";
import { BookOpen, Code2, Search, Tag, X } from "lucide-react";
import { rulesApi, type LibraryEntry } from "../../api/rules";
import { GatedButton } from "./RulesPermissionNotice";

interface RuleLibraryGalleryProps {
  entries: LibraryEntry[];
  /** Project to spawn the reused rule into. */
  targetProjectId: string;
  /** Called after a successful reuse so the parent can refresh My Rules. */
  onReused: (newRuleId: string) => void;
  /** When set the user may not add rules (reuse = POST /customize needs
   * `rules:create`): "Reuse in project" is disabled and this is its tooltip
   * and a line under the filters. */
  reuseDisabledReason?: string | null;
  /** Open the entry's code (SQL / JSON). */
  onViewCode?: (entry: LibraryEntry) => void;
}

// Friendly labels for the structured Functional-Rules-Framework RuleType
// values (origin=project|library DB rules). KB/condition_sql rules (origin=
// knowledge_base) have no RuleType at all — the API maps their DAMA
// `category` (validity/completeness/uniqueness/consistency/accuracy/
// timeliness/integrity) into this same `rule_type` field for display
// purposes (see unifiedToLibraryEntry in api/rules.ts), so `entry.rule_type`
// can hold either taxonomy depending on origin. A hardcoded option list
// covering only the structured values silently filtered out every KB rule
// (0 results for any specific filter) — the fallback below humanizes
// whatever raw value is actually present instead of assuming one taxonomy.
const RULE_TYPE_LABELS: Record<string, string> = {
  referential_integrity: "Referential integrity",
  range_domain: "Range / domain",
  conditional: "Conditional",
  temporal_consistency: "Temporal consistency",
  cross_table_aggregate: "Cross-table aggregate",
  format_pattern: "Format / pattern",
  statistical_distribution: "Statistical distribution",
};

function humanizeRuleType(value: string): string {
  return (
    RULE_TYPE_LABELS[value] ??
    value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function RuleLibraryGallery({
  entries,
  targetProjectId,
  onReused,
  reuseDisabledReason = null,
  onViewCode,
}: RuleLibraryGalleryProps) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [tagFilter, setTagFilter] = useState<string>("");
  const [reusing, setReusing] = useState<string | null>(null);
  const [reuseTarget, setReuseTarget] = useState<LibraryEntry | null>(null);
  const [labelOverride, setLabelOverride] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Unique tag index for the chip filter
  const allTags = useMemo(() => {
    const s = new Set<string>();
    entries.forEach((e) => e.domain_tags.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [entries]);

  // Type-filter options, derived from whatever values are actually present
  // (not hardcoded) — see RULE_TYPE_LABELS above for why.
  const typeOptions = useMemo(() => {
    const s = new Set<string>();
    entries.forEach((e) => e.rule_type && s.add(e.rule_type));
    return Array.from(s).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (typeFilter && e.rule_type !== typeFilter) return false;
      if (tagFilter && !e.domain_tags.includes(tagFilter)) return false;
      if (!q) return true;
      const hay =
        `${e.label} ${e.description ?? ""} ${e.nl_text ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, search, typeFilter, tagFilter]);

  const openReuse = (entry: LibraryEntry) => {
    setReuseTarget(entry);
    setLabelOverride(entry.label);
    setErr(null);
  };

  const confirmReuse = async () => {
    if (!reuseTarget) return;
    try {
      setReusing(reuseTarget.id);
      setErr(null);
      const result = await rulesApi.library.reuse(reuseTarget.id, {
        target_project_id: targetProjectId,
        label_override:
          labelOverride && labelOverride !== reuseTarget.label
            ? labelOverride
            : undefined,
      });
      setReuseTarget(null);
      onReused(result.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setReusing(null);
    }
  };

  return (
    <div className="space-y-4">
      {reuseDisabledReason && (
        <p className="text-xs text-[var(--warning)]">
          {reuseDisabledReason} to reuse a library rule in a project.
        </p>
      )}
      {/* ── Search + filters ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search library — label, description, or NL text…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 text-sm text-white/90 placeholder:text-white/40 outline-none focus:ring-1 focus:ring-[var(--primary)]"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-md bg-white/5 text-sm text-white/90 outline-none focus:ring-1 focus:ring-[var(--primary)]"
        >
          <option value="">All types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {humanizeRuleType(t)}
            </option>
          ))}
        </select>

        <div className="ml-auto text-xs text-white/55">
          {filtered.length} / {entries.length} entr
          {filtered.length === 1 ? "y" : "ies"}
        </div>
      </div>

      {/* ── Tag chips ────────────────────────────────────────────────── */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag className="w-3 h-3 text-white/45" />
          <button
            onClick={() => setTagFilter("")}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              tagFilter === ""
                ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]"
                : "border-white/15 text-white/55 hover:text-white/85"
            }`}
          >
            all
          </button>
          {allTags.map((t) => (
            <button
              key={t}
              onClick={() => setTagFilter(tagFilter === t ? "" : t)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                tagFilter === t
                  ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]"
                  : "border-white/15 text-white/55 hover:text-white/85"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* ── Gallery grid ─────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="glass-surface rounded-xl p-10 text-center">
          <BookOpen className="w-6 h-6 text-white/30 mx-auto mb-2" />
          <p className="text-sm text-white/60">
            No library entries match the current filters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((entry) => (
            <article
              key={entry.id}
              className="glass-surface rounded-xl p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-medium text-white/90 leading-snug">
                  {entry.label}
                </h4>
                <span
                  className="shrink-0 text-[11px] tracking-wider text-white/50 text-right"
                  title="Knowledge pack this rule ships in"
                >
                  {entry.pack_name ? `${entry.pack_name} ` : ""}v{entry.version}
                </span>
              </div>
              {entry.description && (
                <p className="text-xs text-white/55 line-clamp-3">
                  {entry.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/65">
                  {entry.rule_type.replace(/_/g, " ")}
                </span>
                {entry.domain_tags.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-white/5 text-white/55"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-auto flex items-center justify-between pt-2 border-t border-white/5">
                <div className="text-[11px] text-white/45">
                  reused {entry.reuse_count}×
                </div>
                <div className="flex items-center gap-1">
                  {onViewCode && (
                    <button
                      onClick={() => onViewCode(entry)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md text-white/65 hover:text-white/90 hover:bg-white/5"
                    >
                      <Code2 className="w-3 h-3" />
                      View code
                    </button>
                  )}
                  <GatedButton
                    gate={{
                      allowed: !reuseDisabledReason,
                      permission: "rules:create",
                      reason: reuseDisabledReason,
                    }}
                    onClick={() => openReuse(entry)}
                    disabled={reusing === entry.id}
                    className="text-xs px-3 py-1.5 rounded-md bg-[var(--primary)]/15 hover:bg-[var(--primary)]/25 text-[var(--primary)] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reusing === entry.id ? "Reusing…" : "Reuse in project"}
                  </GatedButton>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* ── Reuse confirmation dialog ────────────────────────────────── */}
      {reuseTarget && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Reuse rule from library"
        >
          <div className="glass-surface-heavy rounded-2xl p-5 max-w-md w-full space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold text-white/90">
                Reuse library rule
              </h3>
              <button
                onClick={() => setReuseTarget(null)}
                className="text-white/50 hover:text-white/90"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-white/60">
              A new draft rule will be created in this project from the selected
              library entry. You can edit or submit it for approval afterwards.
            </p>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-white/50">
                Label
              </label>
              <input
                type="text"
                value={labelOverride}
                onChange={(e) => setLabelOverride(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-white/5 text-sm text-white/90 outline-none focus:ring-1 focus:ring-[var(--primary)]"
              />
            </div>
            {err && (
              <p className="text-xs text-red-300 bg-red-400/10 border border-red-400/30 rounded-md px-3 py-2">
                {err}
              </p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setReuseTarget(null)}
                className="px-3 py-1.5 text-xs rounded-md text-white/70 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmReuse}
                disabled={!labelOverride.trim() || reusing === reuseTarget.id}
                className="px-4 py-1.5 text-xs rounded-md bg-[var(--primary)]/80 hover:bg-[var(--primary)] text-white font-medium disabled:opacity-50"
              >
                {reusing === reuseTarget.id ? "Reusing…" : "Reuse"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
